import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { getUserProfile } from '@/api/user';
import { useAuth, validateSession } from '@/composables/useAuth';
import { usePortfolioBalanceStore } from '@/stores/portfolioBalance';

function formatNavUsd(value) {
  if (value == null) return '…';
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return `$${n.toFixed(2)}`;
}

/** App 顶栏与账户页共用，避免编辑资料后顶栏头像不同步 */
const profile = ref(null);
const profileLoading = ref(false);

/**
 * 用已保存的 profile 立刻更新顶栏（含头像），不必再等 GET。
 * @param {object|null|undefined} next
 */
export function applyNavUserProfile(next) {
  if (next && typeof next === 'object') {
    profile.value = next;
  }
}

export function useNavUserSummary() {
  const { isLoggedIn } = useAuth();
  const portfolioStore = usePortfolioBalanceStore();
  const {
    totalAssets,
    platformAvailableDisplay,
    platformAvailable,
    loading: portfolioLoading,
  } = storeToRefs(portfolioStore);

  const totalAssetsLabel = computed(() => {
    if (isLoggedIn.value && platformAvailable.value == null) return '…';
    return formatNavUsd(totalAssets.value ?? 0);
  });

  /** 余额：平台 DUSD 可用（展示 USD）；不再用链上 USDC */
  const walletBalanceLabel = computed(() => {
    if (isLoggedIn.value && platformAvailable.value == null) return '…';
    return formatNavUsd(platformAvailableDisplay.value ?? 0);
  });

  const platformAvailableLabel = computed(() => {
    if (isLoggedIn.value && platformAvailable.value == null) return '…';
    return formatNavUsd(platformAvailableDisplay.value ?? 0);
  });

  /** @deprecated 兼容旧引用，等同 totalAssetsLabel */
  const balanceLabel = totalAssetsLabel;

  const loading = computed(() => portfolioLoading.value || profileLoading.value);

  async function refresh(silent = false, forceBalance = false) {
    if (!isLoggedIn.value || !validateSession()) {
      profile.value = null;
      portfolioStore.reset();
      profileLoading.value = false;
      return;
    }

    profileLoading.value = true;

    try {
      const profilePromise = getUserProfile()
        .then((res) => {
          profile.value = res ?? null;
        })
        .catch((err) => {
          console.warn('[nav] getUserProfile failed', err);
          profile.value = null;
        })
        .finally(() => {
          profileLoading.value = false;
        });

      const balancePromise = portfolioStore.refresh({
        force: forceBalance,
        silent,
      });

      await Promise.all([profilePromise, balancePromise]);
    } finally {
      profileLoading.value = false;
    }
  }

  watch(
    isLoggedIn,
    (loggedIn) => {
      if (loggedIn) refresh(true);
      else {
        profile.value = null;
        portfolioStore.reset();
      }
    },
    { immediate: true }
  );

  function onVisibilityChange() {
    if (document.visibilityState === 'visible' && isLoggedIn.value) {
      refresh(true);
    }
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  return {
    profile,
    totalAssetsLabel,
    platformAvailableLabel,
    walletBalanceLabel,
    balanceLabel,
    loading,
    profileLoading,
    refresh,
    applyProfile: applyNavUserProfile,
  };
}
